import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { MotionConfig } from "framer-motion"
import { useEffect } from "react"
import { RouterProvider } from "react-router-dom"
import { toast } from "sonner"
import { NoSubscriptionsScreen } from "@/app/ui/no-subscriptions-screen"
import { useAuth } from "@/entities/session"
import { useSubscriptionStatus, useSyncSubscriptions } from "@/entities/subscription"
import { TourProvider } from "@/features/onboarding-tour"
import { usePointerEventsGuard } from "@/shared/hooks/use-pointer-events-guard"
import { isDevMode } from "@/shared/lib/env"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { Toaster } from "@/shared/ui/sonner"
import { TooltipProvider } from "@/shared/ui/tooltip"
import { router } from "./routes/router"

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5,
			refetchOnWindowFocus: false,
		},
	},
})

function AppContent() {
	const { isAuthenticating, isAuthenticated, error } = useAuth()
	const isDev = isDevMode()
	const {
		data: subscriptionStatus,
		isLoading: isLoadingSubscriptions,
		isError: subscriptionError,
		refetch: refetchSubscriptions,
	} = useSubscriptionStatus(!isDev && isAuthenticated)
	const syncSubscriptions = useSyncSubscriptions()

	useEffect(() => {
		if (!isAuthenticated || isDev) return

		let mounted = true

		const doSync = async () => {
			try {
				await syncSubscriptions.mutateAsync()
				if (mounted) await refetchSubscriptions()
			} catch {
				if (mounted) {
					toast.error("Не удалось синхронизировать подписки")
				}
			}
		}

		doSync()

		return () => {
			mounted = false
		}
	}, [isAuthenticated, isDev, refetchSubscriptions, syncSubscriptions.mutateAsync])

	if (isLoadingSubscriptions || isAuthenticating) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center">
				<div className="text-center space-y-4">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
					<p className="text-sm text-muted-foreground">
						{isAuthenticating ? "Входим в приложение…" : "Проверяем доступ…"}
					</p>
				</div>
			</div>
		)
	}

	if (!isAuthenticated) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center">
				<div className="text-center space-y-4 p-8">
					<p className="text-lg text-muted-foreground">{error ?? "Не удалось авторизоваться"}</p>
					<p className="text-sm text-muted-foreground">
						Пожалуйста, откройте приложение через Telegram
					</p>
				</div>
			</div>
		)
	}

	if (!isDev && subscriptionError && !subscriptionStatus)
		return (
			<LoadErrorState
				message="Не удалось проверить доступ"
				onRetry={() => void refetchSubscriptions()}
			/>
		)

	if (!isDev && subscriptionStatus && !subscriptionStatus.hasSubscriptions) {
		return <NoSubscriptionsScreen onRetry={() => refetchSubscriptions()} />
	}

	return <RouterProvider router={router} />
}

function App() {
	usePointerEventsGuard()
	return (
		<QueryClientProvider client={queryClient}>
			<MotionConfig reducedMotion="user">
				<TooltipProvider delayDuration={200}>
					<TourProvider>
						<AppContent />
						<Toaster position="bottom-center" />
					</TourProvider>
				</TooltipProvider>
			</MotionConfig>
		</QueryClientProvider>
	)
}

export default App
